import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "./config";
import { getEcho } from "./echo";
import ChatModal from "./ChatModal";
import { alertNewMessage, ensureNotificationPermission } from "./chatNotify";

// Darken (pct<0) / lighten (pct>0) a #rrggbb hex — used to build the mobile
// notification page's gradient app-bar from the community's primary color.
function shade(hex, pct = -18) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return hex;
  const num = parseInt(m[1], 16);
  const t = pct < 0 ? 0 : 255;
  const p = Math.abs(pct) / 100;
  const r = Math.round(((num >> 16) & 255) * (1 - p) + t * p);
  const g = Math.round(((num >> 8) & 255) * (1 - p) + t * p);
  const b = Math.round((num & 255) * (1 - p) + t * p);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

// Small avatar (image or initial) for a notification's actor.
function ActorAvatar({ name, img, primary }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  return (
    <span
      className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full text-sm font-bold text-white"
      style={{ backgroundColor: primary }}
    >
      {img ? <img src={img} alt="" className="h-full w-full object-cover" /> : initial}
    </span>
  );
}

// Header bell with an unread badge + dropdown of recent notifications. Polls the
// unread count; opening the panel loads the list (items stay unread until you
// click "Mark all as read" or open a notification).
export default function NotificationBell({ token, member, primary = "#0274F7" }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  // member_token of a chat opened from a message notification; null = closed.
  const [chatTarget, setChatTarget] = useState(null);
  const ref = useRef(null);
  const navigate = useNavigate();
  // Bumped on every local read mutation. A fetchList() captures its value at the
  // start and discards its result if it changed while the request was in flight,
  // so a slow background poll can't revert an optimistic "mark as read".
  const mutationRef = useRef(0);

  const memberToken = member?.auth_token;
  const memberId = member?.community_member_id;

  const fetchList = useCallback(async () => {
    if (!token || !memberToken) return;
    const mutationAtStart = mutationRef.current;
    setLoading(true);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-notifications`, {
        community_token: token,
        member_token: memberToken,
      });
      // A read mutation happened while this poll was in flight → its data is stale
      // and would revert the optimistic update; drop it.
      if (mutationAtStart !== mutationRef.current) return;
      if (res.data?.error === 0) {
        setList(res.data.notifications || []);
        setUnread(res.data.unread || 0);
      }
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [token, memberToken]);

  // Poll the unread count (and list) while signed in. This is the fallback for
  // when the Reverb WebSocket isn't connected — kept fairly short so a new
  // message tag still shows up quickly without real-time.
  useEffect(() => {
    if (!token || !memberToken) return undefined;
    fetchList();
    const t = setInterval(fetchList, 20000);
    return () => clearInterval(t);
  }, [token, memberToken, memberId, fetchList]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      // Desktop dropdown only. The mobile full-page panel is portaled outside
      // `ref` and closes via its own back button — skip the outside-click close
      // there, else tapping inside it (e.g. "Mark all read") would dismiss it.
      if (window.matchMedia("(max-width: 639px)").matches) return;
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Lock body scroll behind the mobile full-page panel (no effect on desktop
  // where it renders as a dropdown).
  useEffect(() => {
    if (!open) return undefined;
    const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches;
    if (!isMobile) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Live badge: subscribe to this member's personal channel so a new DM lights
  // up the bell instantly (no waiting for the next poll).
  useEffect(() => {
    if (!token || !memberToken || !memberId) return undefined;
    const echo = getEcho({ communityToken: token, memberToken });
    if (!echo) return undefined;
    const channel = `member.${memberId}`;
    const handler = (payload) => {
      // Our device received it → ack delivery so the sender's ticks go double.
      // This happens even for muted (silent) conversations.
      if (payload?.conversation_id) {
        axios
          .post(`${config.COMMUNITY_API_URL}/community-chat-delivered`, {
            community_token: token,
            member_token: memberToken,
            conversation_id: payload.conversation_id,
          })
          .catch(() => {});
      }
      // Muted conversations ping silently: ack delivery only, no badge/sound/alert.
      if (payload?.silent) return;
      fetchList();
      alertNewMessage(payload?.actor_name || "New message", payload?.preview || "");
    };
    echo.private(channel).listen(".message.notification", handler);
    return () => {
      try {
        echo.leave(channel);
      } catch (e) {
        /* ignore */
      }
    };
  }, [token, memberToken, memberId, fetchList]);

  // Mark a single notification read (optimistic local update + persist).
  const markOneRead = (n) => {
    if (n.is_read) return;
    mutationRef.current += 1;
    setUnread((u) => Math.max(0, u - 1));
    setList((l) => l.map((x) => (x.id === n.id ? { ...x, is_read: 1 } : x)));
    axios
      .post(`${config.COMMUNITY_API_URL}/community-notifications-read`, {
        community_token: token,
        member_token: memberToken,
        notification_id: n.id,
      })
      .catch(() => {});
  };

  // Open the chat thread for a "new message" notification.
  const openChat = (n) => {
    if (!n.actor_token) return;
    setOpen(false);
    markOneRead(n);
    setChatTarget(n.actor_token);
  };

  // Deep-link to a post-related notification's post on its group feed.
  const openPost = (n) => {
    if (!n.post_id || !n.group_token) return;
    setOpen(false);
    markOneRead(n);
    navigate(`/${token}?gid=${n.group_token}&page=feed&post=${n.post_id}`);
  };

  const markAllRead = async () => {
    if (!unread) return;
    mutationRef.current += 1;
    setUnread(0);
    setList((l) => l.map((n) => ({ ...n, is_read: 1 })));
    try {
      await axios.post(`${config.COMMUNITY_API_URL}/community-notifications-read`, {
        community_token: token,
        member_token: memberToken,
      });
    } catch {
      /* ignore */
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      ensureNotificationPermission();
      fetchList();
    }
  };

  if (!member) return null;

  // One notification row — shared by the desktop dropdown and the mobile page.
  const renderItem = (n) => {
    const isMessage = n.type === "message" && !!n.actor_token;
    const isPost = !!n.post_id && !!n.group_token;
    const clickable = isMessage || isPost;
    const onClick = isMessage ? () => openChat(n) : isPost ? () => openPost(n) : undefined;
    return (
      <li
        key={n.id}
        onClick={onClick}
        className={`relative flex items-start gap-3 py-3.5 pr-4 transition-colors ${
          n.is_read ? "pl-4 hover:bg-gray-50" : "pl-[13px] hover:bg-black/[0.02]"
        } ${clickable ? "cursor-pointer active:bg-black/[0.04]" : ""}`}
        style={n.is_read ? undefined : { backgroundColor: `${primary}0d` }}
      >
        {!n.is_read && (
          <span className="absolute inset-y-0 left-0 w-1" style={{ backgroundColor: primary }} aria-hidden="true" />
        )}
        <ActorAvatar name={n.actor_name} img={n.actor_img} primary={primary} />
        <div className="min-w-0 flex-1">
          <p className={`text-sm leading-snug ${n.is_read ? "text-gray-600" : "font-semibold text-gray-900"}`}>
            {n.text}
          </p>
          <p className={`mt-0.5 text-xs ${n.is_read ? "text-gray-400" : "font-medium text-gray-500"}`}>
            {n.time_ago}
          </p>
        </div>
        {!n.is_read && (
          <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-white" style={{ backgroundColor: primary }} />
        )}
      </li>
    );
  };

  const emptyState = (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-full" style={{ backgroundColor: `${primary}14` }}>
        <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke={primary} strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
      </span>
      <div>
        <p className="text-sm font-bold text-gray-800">You&apos;re all caught up</p>
        <p className="mt-0.5 text-xs text-gray-400">New notifications will show up here.</p>
      </div>
    </div>
  );

  const bodyList =
    loading && list.length === 0 ? (
      <p className="py-12 text-center text-sm text-gray-400">Loading…</p>
    ) : list.length === 0 ? (
      emptyState
    ) : (
      <ul className="divide-y divide-gray-50">{list.map(renderItem)}</ul>
    );

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-label="Notifications"
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition"
      >
        <svg className="h-[22px] w-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white ring-2 ring-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {/* Desktop (≥sm): compact dropdown anchored to the bell. */}
      {open && (
        <div className="hidden sm:block absolute right-0 mt-2 z-[2000] w-80 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl animate-fade-in">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <h3 className="text-sm font-bold text-gray-900">Notifications</h3>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="text-xs font-semibold hover:underline"
                style={{ color: primary }}
              >
                Mark all as read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">{bodyList}</div>
        </div>
      )}

      {/* Mobile (<sm): full-page screen with a premium gradient app-bar.
          Portaled to <body> so an ancestor header's backdrop-blur (which creates
          a containing block) can't trap the fixed overlay inside the header. */}
      {open && createPortal(
        <div className="sm:hidden fixed inset-0 z-[3000] flex flex-col bg-gray-50 animate-slide-up">
          <div
            className="relative flex items-center gap-3 px-4 pb-4 pt-[max(1rem,env(safe-area-inset-top))] text-white shadow-lg"
            style={{ backgroundImage: `linear-gradient(135deg, ${primary}, ${shade(primary, -22)})` }}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close notifications"
              className="-ml-1.5 flex h-9 w-9 items-center justify-center rounded-full text-white/90 transition hover:bg-white/15 active:scale-95"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold leading-tight">Notifications</h2>
              <p className="text-xs font-medium text-white/80">
                {unread > 0 ? `${unread} unread` : "You're all caught up"}
              </p>
            </div>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="shrink-0 rounded-full bg-white/20 px-3 py-1.5 text-xs font-semibold backdrop-blur-sm transition hover:bg-white/30 active:scale-95"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto overscroll-contain">
            {list.length === 0 ? (
              <div className="flex min-h-full items-center justify-center">{bodyList}</div>
            ) : (
              <div className="p-3">
                <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">{bodyList}</div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {chatTarget && (
        <ChatModal
          token={token}
          member={member}
          primary={primary}
          startWith={chatTarget}
          onClose={() => {
            setChatTarget(null);
            fetchList();
          }}
        />
      )}
    </div>
  );
}
